import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Logo3D from "@/components/hud/Logo3D";
import { Seo } from "@/components/Seo";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Uplink established");
      } else {
        await register(form.name, form.email, form.password);
        toast.success("Identity forged");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const field = (key, label, type = "text") => (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <input
        type={type}
        required
        value={form[key]}
        data-testid={`auth-${key}-input`}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-1.5 w-full bg-cyan/5 border-b border-cyan/25 focus:border-cyan text-slate-100 px-3 py-2.5 font-mono text-sm outline-none transition-colors"
      />
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-app relative overflow-hidden px-5">
      <Seo title="Sign In / Register" path="/auth" noindex />
      <div className="absolute inset-0 hud-grid opacity-30" />
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 opacity-40">
        <Logo3D size={200} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md glass-strong clip-chamfer brackets p-8 mt-24"
        data-testid="auth-panel"
      >
        <div className="text-center mb-7">
          <div className="font-mono text-[10px] tracking-[0.35em] text-cyan/70">SECURE ACCESS TERMINAL</div>
          <h1 className="font-head text-3xl uppercase font-bold mt-2 text-glow-cyan">
            {mode === "login" ? "Re-Establish Link" : "Forge Identity"}
          </h1>
        </div>

        <div className="flex mb-6 border border-cyan/20">
          {["login", "register"].map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              data-testid={`auth-tab-${m}`}
              className={`flex-1 py-2 font-head text-xs uppercase tracking-[0.15em] transition-colors ${
                mode === m ? "bg-cyan text-app" : "text-slate-400 hover:text-cyan"
              }`}
            >
              {m === "login" ? "Sign In" : "Register"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-5">
          {mode === "register" && field("name", "Operator Name")}
          {field("email", "Comlink / Email", "email")}
          {field("password", "Passkey", "password")}
          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-btn"
            className="btn-hud w-full border border-cyan text-cyan py-3 text-sm box-glow-cyan hover:bg-cyan hover:text-app disabled:opacity-50"
          >
            {busy ? "PROCESSING..." : mode === "login" ? "Initiate Uplink" : "Create Account"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-cyan/15" />
          <span className="font-mono text-[10px] text-slate-600">OR</span>
          <div className="flex-1 h-px bg-cyan/15" />
        </div>

        <button
          onClick={googleLogin}
          data-testid="google-login-btn"
          className="btn-hud w-full border border-slate-600 text-slate-200 py-3 text-sm hover:border-amber hover:text-amber flex items-center justify-center gap-2"
        >
          <GoogleIcon /> Continue with Google
        </button>
      </motion.div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8a12 12 0 010-24c3 0 5.8 1.1 7.9 3l5.7-5.7A20 20 0 1024 44a20 20 0 0019.6-23.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0124 12c3 0 5.8 1.1 7.9 3l5.7-5.7A20 20 0 006.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3A12 12 0 0124 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5A20 20 0 0024 44z" />
      <path fill="#1976D2" d="M43.6 20.5H24v8h11.3a12 12 0 01-4.1 5.5l6.3 5.3C41 36.5 44 31 44 24c0-1.2-.1-2.4-.4-3.5z" />
    </svg>
  );
}
